import { useLayoutEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { DATE_RANGE_TYPES } from '../../utils/dates';
import DateRange from '../../common/DangeRange/DateRange';

import Button from "../../common/Button/Button";
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';

import { ReactComponent as InfoIcon } from '../../assets/icons_svg/info.svg';
import './LeadRangeDropdown.scss';

export default function LeadRangeDropdown({ children, toggle, dateRange, setDateRange, earliestDate }) {
  const [show, setShow] = useState(false);
  const [dateType, setDateType] = useState(null);
  const [filterType, setFilterType] = useState('date_added');
  const [toDate, setToDate] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [customSelected, setCustomSelected] = useState(false);

  const { t } = useTranslation();

  useLayoutEffect(() => {
    if (show) {
      setDateType(dateRange?.type);
      setCustomSelected(dateRange?.type === 'custom');

      if (dateRange) {
        setFromDate(dateRange.dates[0].value);
        setToDate(dateRange.dates[1]?.value || DateTime.now().toISODate());
      }
      // date range was cleared by "clear range" on the list view or such:
      else {
        setFromDate(null);
        setToDate(null);
      }
    }
  }, [show, dateRange, earliestDate]);

  const onSetCustomDate = () => {
    setDateRange({
      type: 'custom',
      dates: [
        { field : filterType + '_after', value : DateTime.fromISO(fromDate).startOf('day').toISO() },
        { field : filterType + '_before', value : DateTime.fromISO(toDate || fromDate).endOf('day').toISO() },
      ]
    });
    setShow(false);
  }

  const onSetFixedDates = type => {
    const range = { type };

    type !== 'custom' && setCustomSelected(false);

    switch (type) {
      case 'all_time':
        setDateRange(null);
        setFromDate(null);
        setToDate(null);
        setShow(false);
        return;

      // when selecting custom, don't close the menu
      case 'custom':
        setCustomSelected(true);
        return;

      case 'today':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().startOf('day').toISO() }];
        break;

      case 'yesterday':
        range.dates = [
          { field: filterType + '_after', value: DateTime.now().minus({ days: 1 }).startOf('day').toISO() },
          { field: filterType + '_before', value: DateTime.now().minus({ days: 1 }).endOf('day').toISO() }
        ];
        break;
      
      case 'today_yesterday':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().minus({ days: 1 }).toISO() }];
        break;

      case 'this_week':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().startOf('week').toISO() }];
        break;

      case 'last_week':
        range.dates = [
          { field: filterType + '_after', value: DateTime.now().minus({ weeks: 1 }).startOf('week').toISO() },
          { field: filterType + '_before', value: DateTime.now().minus({ weeks: 1 }).endOf('week').toISO() }
        ]
        break;
      
      case 'this_month':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().startOf('month').toISO() }];
        break;

      case 'last_month':
        range.dates = [
          { field: filterType + '_after', value: DateTime.now().minus({ months: 1 }).startOf('month').toISO() },
          { field: filterType + '_before', value: DateTime.now().minus({ months: 1 }).endOf('month').toISO() }
        ];
        break;

      case 'last_7':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().minus({ days: 7 }).toISO() }];
        break;

      case 'last_14':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().minus({ days: 14 }).toISO() }];
        break;

      case 'last_30':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().minus({ days: 30 }).toISO() }];
        break;

      case 'last_60':
        range.dates = [{ field: filterType + '_after', value: DateTime.now().minus({ days: 60 }).toISO() }];
        break;

      default:
        console.warn(`Unset type of fixed dates: '${type}'`);
        return null;
    }

    setDateRange(range);
    setShow(false);
  }

  const onChangeFrom = date => {
    setFromDate(date);
    setCustomSelected(true);
  }
  
  const onChangeTo = date => {
    setToDate(date);
    setCustomSelected(true);
  }

  const fromDateText = DateTime.fromISO(fromDate || earliestDate).toLocaleString(DateTime.DATE_MED);
  const toDateText = (toDate || fromDate ? DateTime.fromISO(toDate || fromDate) : DateTime.now()).toLocaleString(DateTime.DATE_MED);

  return (
    <Dropdown className='__dropdown actions-dropdown __lead-range-dropdown' align='end' show={show} onToggle={() => setShow(!show)}>
      <Dropdown.Toggle as={toggle}>
        { children }
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <div className='menu-container'>
          <div className='preset-dates'>
            { Object.entries(DATE_RANGE_TYPES).map(([key, value]) => (
              <Form.Check key={key} type='radio' label={t(value)} value={key} checked={customSelected ? key === 'custom' : (dateType === key || (!dateType&& key === 'all_time')) } onChange={() => onSetFixedDates(key)} />
            ))}
          </div>

          <div className='custom-range'>
            <div className='range-type-dates'>
              <Form.Select value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value='date_added'>Date Created</option>
                <option value='last_updated'>Last Updated</option>
              </Form.Select>

              <Form.Control type='text' value={fromDateText} readOnly />
              <span>{' — '}</span>
              <Form.Control type='text' value={toDateText} readOnly />
            </div>
            { filterType === 'last_updated' && (
              <div className='range-type-description'>
                <InfoIcon />
                <span>Any lead that had any kind of update in any of its fields, status, or assignee from the selected dates will be displayed. <a href="#WIP">Learn more.</a></span>
              </div>
            )}

            { earliestDate && <DateRange dateRange={dateRange} from={fromDate} onChangeFrom={onChangeFrom} to={toDate} onChangeTo={onChangeTo} earliestDate={earliestDate} /> }

            <div className='range-footer'>
              <Button variant='secondary' onClick={() => setShow(false)}>Cancel</Button>
              <Button variant='primary' onClick={onSetCustomDate} disabled={!fromDate}>Update</Button>
            </div>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}
